<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Application FTP communication status
const communicationStatus = ref({
    activeDevices: 18,
    totalDevices: 24,
    ftpConnections: 15,
    maxConnections: 25,
    averageSpeed: 1.8, // MB/s
    storageUsed: 468, // GB
    storageTotal: 1024, // GB
    lastUpdate: '15/04/25 09:23'
})

// Historical data for charts
const historicalSpeed = ref([
    { time: '03:00', value: 1.2 },
    { time: '06:00', value: 0.9 },
    { time: '09:00', value: 1.8 },
    { time: '12:00', value: 2.2 },
    { time: '15:00', value: 2.4 },
    { time: '18:00', value: 1.5 },
    { time: '21:00', value: 1.1 },
    { time: '00:00', value: 0.8 }
])

const dataTransfer = ref([
    { time: '03:00', uploaded: 0.8, downloaded: 1.4 },
    { time: '06:00', uploaded: 0.5, downloaded: 1.1 },
    { time: '09:00', uploaded: 1.2, downloaded: 2.3 },
    { time: '12:00', uploaded: 1.8, downloaded: 2.7 },
    { time: '15:00', uploaded: 1.5, downloaded: 2.4 },
    { time: '18:00', uploaded: 1.1, downloaded: 1.9 },
    { time: '21:00', uploaded: 0.7, downloaded: 1.3 },
    { time: '00:00', uploaded: 0.4, downloaded: 1.0 }
])

// FTP Devices
const ftpDevices = ref([
    {
        id: 1,
        name: 'StorageUnit-A1',
        location: 'Atacama, Centro',
        storageUsed: 128,
        storageTotal: 256,
        status: 'activo', // Keep user-facing text in Spanish
        statusColor: 'success',
        lastSync: '15/04/25 09:21'
    },
    {
        id: 2,
        name: 'DataLogger-B24',
        location: 'Valparaíso, Quillota',
        storageUsed: 78,
        storageTotal: 128,
        status: 'activo',
        statusColor: 'success',
        lastSync: '15/04/25 09:15'
    },
    {
        id: 3,
        name: 'StoreServer-C12',
        location: 'O\'Higgins, San Fernando',
        storageUsed: 182,
        storageTotal: 256,
        status: 'degradado',
        statusColor: 'warning',
        lastSync: '15/04/25 08:45'
    },
    {
        id: 4,
        name: 'DataUnit-D5',
        location: 'Metropolitana, Lampa',
        storageUsed: 110,
        storageTotal: 128,
        status: 'crítico',
        statusColor: 'error',
        lastSync: '14/04/25 23:12'
    },
    {
        id: 5,
        name: 'BackupNode-E3',
        location: 'Biobío, Concepción',
        storageUsed: 0,
        storageTotal: 256,
        status: 'inactivo',
        statusColor: 'error',
        lastSync: '14/04/25 15:30'
    }
])

// Recent Errors
const recentErrors = ref([
    {
        id: 1,
        device: 'DataUnit-D5',
        message: 'Espacio de almacenamiento crítico (85%)', // Keep user-facing text in Spanish
        timeAgo: '8m',
        code: 'STORAGE_CRITICAL',
        type: 'warning'
    },
    {
        id: 2,
        device: 'BackupNode-E3',
        message: 'Conexión FTP rechazada',
        timeAgo: '38m',
        code: 'FTP_CONNECTION_REFUSED',
        type: 'error'
    },
    {
        id: 3,
        device: 'StoreServer-C12',
        message: 'Transferencia completada con advertencias',
        timeAgo: '1h',
        code: 'TRANSFER_WARNING',
        type: 'warning'
    }
])

// FTP Operation Distribution
const operationDistribution = ref([
    { type: 'Descargas', count: 324, color: '#4CAF50' }, // Keep user-facing text in Spanish
    { type: 'Subidas', count: 158, color: '#2196F3' },
    { type: 'Listados', count: 487, color: '#FF9800' },
    { type: 'Eliminación', count: 95, color: '#F44336' }
])

// Data Flows between components
const dataFlows = ref([
    {
        id: 1,
        source: 'Sensores de Campo', // Keep user-facing text in Spanish
        destination: 'StorageUnit-A1',
        connectionType: 'FTP',
        status: 'activo',
        statusColor: 'success',
        rate: '1.2 MB/s'
    },
    {
        id: 2,
        source: 'DataLogger-B24',
        destination: 'Servidor Central',
        connectionType: 'FTP',
        status: 'activo',
        statusColor: 'success',
        rate: '0.8 MB/s'
    },
    {
        id: 3,
        source: 'StoreServer-C12',
        destination: 'Base de Datos',
        connectionType: 'FTP/SFTP',
        status: 'degradado',
        statusColor: 'warning',
        rate: '0.5 MB/s'
    },
    {
        id: 4,
        source: 'DataUnit-D5',
        destination: 'Servicio de Respaldo',
        connectionType: 'FTP',
        status: 'crítico',
        statusColor: 'error',
        rate: '0.2 MB/s'
    },
    {
        id: 5,
        source: 'Servidor Central',
        destination: 'BackupNode-E3',
        connectionType: 'SFTP',
        status: 'inactivo',
        statusColor: 'error',
        rate: '0 MB/s'
    }
])

// Overall system health status
const systemHealth = computed(() => {
    const operationalDevicesRatio = communicationStatus.value.activeDevices / communicationStatus.value.totalDevices;
    const operationalConnectionsRatio = communicationStatus.value.ftpConnections / communicationStatus.value.maxConnections;
    const storageAvailabilityRatio = 1 - (communicationStatus.value.storageUsed / communicationStatus.value.storageTotal);

    const averageHealth = (operationalDevicesRatio + operationalConnectionsRatio + storageAvailabilityRatio) / 3 * 100;

    if (averageHealth >= 90) return { value: averageHealth, text: 'Excelente', color: 'success' }; // Keep user-facing text in Spanish
    if (averageHealth >= 75) return { value: averageHealth, text: 'Bueno', color: 'success' };
    if (averageHealth >= 60) return { value: averageHealth, text: 'Aceptable', color: 'warning' };
    return { value: averageHealth, text: 'Crítico', color: 'error' };
})

// Refresh data every 30 seconds
const refreshData = () => {
    // API calls or service fetches to get updated data would go here
    console.log('Updating device and storage data...')
}

const deleteData = (device: any) => {
    // Data deletion implementation
    console.log(`Requesting data deletion on: ${device.name}`)
    // Logic to connect via FTP and delete data would go here
}

const syncDevice = (device: any) => {
    // Synchronization implementation
    console.log(`Synchronizing device: ${device.name}`)
    // Logic to connect via FTP and synchronize would go here
}

let intervalId: number | undefined;
onMounted(() => {
    intervalId = setInterval(refreshData, 30000)
})

onBeforeUnmount(() => {
    if (intervalId) {
        clearInterval(intervalId)
    }
})
</script>

<template>
    <v-container fluid>
        <!-- Header -->
        <v-row>
            <v-col cols="12">
                <div class="d-flex justify-space-between align-center mb-6">
                    <div>
                        <h1 class="text-h4 font-weight-bold">Estado de Dispositivos</h1>
                        <p class="text-body-1 text-medium-emphasis">Monitoreo de almacenamiento y conexiones FTP
                        </p>
                    </div>
                    <div>
                        <v-btn color="primary" prepend-icon="mdi-refresh" @click="refreshData">
                            Actualizar
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- Main statistics cards -->
        <v-row>
            <v-col cols="12" sm="6" md="3">
                <v-card class="rounded-lg" flat>
                    <v-card-text class="pb-0">
                        <div class="d-flex align-center">
                            <v-avatar color="#F5F3FF" size="40" class="mr-3">
                                <v-icon color="#8465FF">mdi-harddisk</v-icon>
                            </v-avatar>
                            <div>
                                <div class="text-caption text-medium-emphasis">Dispositivos Activos</div>
                                <div class="text-h5 font-weight-bold">{{ communicationStatus.activeDevices }}/{{
                                    communicationStatus.totalDevices }}</div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-text>
                        <v-progress-linear
                            :model-value="(communicationStatus.activeDevices / communicationStatus.totalDevices) * 100"
                            height="4" color="#8465FF"></v-progress-linear>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="rounded-lg" flat>
                    <v-card-text class="pb-0">
                        <div class="d-flex align-center">
                            <v-avatar color="#EAF6FF" size="40" class="mr-3">
                                <v-icon color="#4CB3FF">mdi-server-network</v-icon>
                            </v-avatar>
                            <div>
                                <div class="text-caption text-medium-emphasis">Conexiones FTP</div>
                                <div class="text-h5 font-weight-bold">{{ communicationStatus.ftpConnections }}/{{
                                    communicationStatus.maxConnections }}</div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-text>
                        <v-progress-linear
                            :model-value="(communicationStatus.ftpConnections / communicationStatus.maxConnections) * 100"
                            height="4" color="#4CB3FF"></v-progress-linear>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="rounded-lg" flat>
                    <v-card-text class="pb-0">
                        <div class="d-flex align-center">
                            <v-avatar color="#F0FBE6" size="40" class="mr-3">
                                <v-icon color="#7ED957">mdi-database</v-icon>
                            </v-avatar>
                            <div>
                                <div class="text-caption text-medium-emphasis">Almacenamiento</div>
                                <div class="text-h5 font-weight-bold">{{ communicationStatus.storageUsed }}/{{
                                    communicationStatus.storageTotal }} GB</div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-text>
                        <v-progress-linear
                            :model-value="(communicationStatus.storageUsed / communicationStatus.storageTotal) * 100"
                            height="4" color="#7ED957"></v-progress-linear>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
                <v-card class="rounded-lg" flat>
                    <v-card-text class="pb-0">
                        <div class="d-flex align-center">
                            <v-avatar color="#FFF8EA" size="40" class="mr-3">
                                <v-icon color="#FFA726">mdi-speedometer</v-icon>
                            </v-avatar>
                            <div>
                                <div class="text-caption text-medium-emphasis">Velocidad Promedio</div>
                                <div class="text-h5 font-weight-bold">{{ communicationStatus.averageSpeed }} MB/s
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-text>
                        <v-progress-linear :model-value="(communicationStatus.averageSpeed / 5) * 100" height="4"
                            color="#FFA726"></v-progress-linear>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Overall health row -->
        <v-row class="mt-4">
            <v-col cols="12">
                <v-card class="rounded-lg">
                    <v-card-title class="d-flex align-center">
                        <v-icon :color="systemHealth.color" size="28" class="mr-2">
                            {{ systemHealth.value >= 75 ? 'mdi-shield-check' : 'mdi-shield-alert' }}
                        </v-icon>
                        Estado de Salud del Sistema
                    </v-card-title>
                    <v-card-text>
                        <div class="d-flex align-center">
                            <div class="flex-grow-1">
                                <v-progress-linear :model-value="systemHealth.value" height="20"
                                    :color="systemHealth.color" rounded>
                                    <template #default="{ value }">
                                        <strong>{{ Math.ceil(value) }}%</strong>
                                    </template>
                                </v-progress-linear>
                            </div>
                            <div class="ml-4">
                                <v-chip :color="systemHealth.color" text-color="white">
                                    {{ systemHealth.text }}
                                </v-chip>
                            </div>
                        </div>
                        <div class="text-caption text-medium-emphasis mt-2">
                            Última actualización: {{ communicationStatus.lastUpdate }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Storage Devices -->
        <v-row class="mt-4">
            <v-col cols="12">
                <h2 class="text-h5 font-weight-bold mb-3">Dispositivos de Almacenamiento</h2>
                <v-card class="rounded-lg">
                    <v-data-table :headers="[
                        { title: 'Dispositivo', key: 'name' },
                        { title: 'Ubicación', key: 'location' },
                        { title: 'Almacenamiento', key: 'storage' }, // Changed key to 'storage' for clarity
                        { title: 'Estado', key: 'status' },
                        { title: 'Última Sincronización', key: 'lastSync' },
                        { title: 'Acciones', key: 'actions', sortable: false },
                    ]" :items="ftpDevices" density="comfortable">
                        <template #[`item.storage`]="{ item }">
                            <div class="d-flex align-center">
                                <v-progress-linear :model-value="(item.storageUsed / item.storageTotal) * 100"
                                    :color="(item.storageUsed / item.storageTotal) > 0.85 ? 'error' : (item.storageUsed / item.storageTotal) > 0.7 ? 'warning' : 'success'"
                                    height="8" rounded class="flex-grow-1 mr-2"></v-progress-linear>
                                <span class="text-caption">{{ item.storageUsed }}/{{ item.storageTotal }}
                                    GB</span>
                            </div>
                        </template>
                        <template #[`item.status`]="{ item }">
                            <v-chip :color="item.statusColor" size="small" text-color="white">
                                {{ item.status }}
                            </v-chip>
                        </template>
                        <template #[`item.actions`]="{ item }">
                            <div class="d-flex">
                                <v-btn icon small class="mr-1" @click="syncDevice(item)">
                                    <v-icon size="small">mdi-sync</v-icon>
                                </v-btn>
                                <v-btn icon small color="error" @click="deleteData(item)">
                                    <v-icon size="small">mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <!-- Data Flows -->
        <v-row class="mt-4">
            <v-col cols="12">
                <h2 class="text-h5 font-weight-bold mb-3">Flujos de Datos</h2>
                <v-card class="rounded-lg">
                    <v-data-table :headers="[
                        { title: 'Origen', key: 'source' },
                        { title: 'Destino', key: 'destination' },
                        { title: 'Protocolo', key: 'connectionType' },
                        { title: 'Estado', key: 'status' },
                        { title: 'Tasa Transferencia', key: 'rate' }
                    ]" :items="dataFlows" density="comfortable">
                        <template #[`item.status`]="{ item }">
                            <v-chip :color="item.statusColor" size="small" text-color="white">
                                {{ item.status }}
                            </v-chip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <!-- Recent Errors and Metrics Panel -->
        <v-row class="mt-4">
            <v-col cols="12" md="6">
                <h2 class="text-h5 font-weight-bold mb-3">Errores Recientes</h2>
                <v-card class="rounded-lg" flat>
                    <v-list>
                        <v-list-item v-for="error in recentErrors" :key="error.id" :ripple="false">
                            <template v-slot:prepend>
                                <v-avatar
                                    :color="error.type === 'error' ? 'error' : error.type === 'warning' ? 'warning' : 'info'"
                                    size="36">
                                    <v-icon color="white">
                                        {{ error.type === 'error' ? 'mdi-alert-circle' : error.type === 'warning' ?
                                            'mdi-alert' : 'mdi-information' }}
                                    </v-icon>
                                </v-avatar>
                            </template>
                            <v-list-item-title>{{ error.message }}</v-list-item-title>
                            <v-list-item-subtitle>
                                {{ error.device }} - {{ error.code }}
                            </v-list-item-subtitle>
                            <template v-slot:append>
                                <v-chip size="small" variant="outlined">{{ error.timeAgo }}</v-chip>
                            </template>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <h2 class="text-h5 font-weight-bold mb-3">Distribución de Operaciones FTP</h2>
                <v-card class="rounded-lg pa-4" height="100%">
                    <div class="d-flex flex-column h-100">
                        <div v-for="(item, i) in operationDistribution" :key="i" class="mb-2 flex-grow-1">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span>{{ item.type }}</span>
                                <span class="text-caption">{{ item.count }}</span>
                            </div>
                            <v-progress-linear
                                :model-value="(item.count / operationDistribution.reduce((acc, curr) => acc + curr.count, 0)) * 100"
                                :color="item.color" height="12" rounded></v-progress-linear>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Charts -->
        <v-row class="mt-4">
            <v-col cols="12" md="6">
                <v-card class="rounded-lg pa-4">
                    <h2 class="text-h6 font-weight-bold mb-4">Velocidad FTP a lo largo del día</h2>
                    <div class="chart-container" style="height: 250px; position: relative;">
                        <!-- A real chart component would go here -->
                        <div class="d-flex align-end" style="height: 200px;">
                            <div v-for="(dataPoint, i) in historicalSpeed" :key="i" class="flex-grow-1 mx-1">
                                <div class="bg-primary rounded-t"
                                    :style="{ height: (dataPoint.value / 3 * 100) + '%' }">
                                </div>
                                <div class="text-caption text-center mt-2">{{ dataPoint.time }}</div>
                            </div>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card class="rounded-lg pa-4">
                    <h2 class="text-h6 font-weight-bold mb-4">Transferencia de Datos (MB/s)</h2>
                    <div class="chart-container" style="height: 250px; position: relative;">
                        <!-- A real chart component would go here -->
                        <div class="d-flex align-end" style="height: 200px;">
                            <div v-for="(dataPoint, i) in dataTransfer" :key="i" class="flex-grow-1 mx-1">
                                <div class="d-flex flex-column">
                                    <div class="bg-success rounded-t"
                                        :style="{ height: (dataPoint.downloaded / 3 * 100) + '%' }">
                                    </div>
                                    <div class="bg-info mt-1 rounded-t"
                                        :style="{ height: (dataPoint.uploaded / 3 * 100) + '%' }">
                                    </div>
                                </div>
                                <div class="text-caption text-center mt-2">{{ dataPoint.time }}</div>
                            </div>
                        </div>
                        <div class="d-flex justify-space-between mt-2">
                            <div class="d-flex align-center">
                                <v-avatar size="10" class="bg-success mr-1"></v-avatar>
                                <span class="text-caption">Descargados</span>
                            </div>
                            <div class="d-flex align-center">
                                <v-avatar size="10" class="bg-info mr-1"></v-avatar>
                                <span class="text-caption">Subidos</span>
                            </div>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>